import React, { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { Send, CheckCircle2, Loader2, Mail, Facebook } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import GoogleIcon from "@/components/GoogleIcon";
import FileUpload from "@/components/site/FileUpload";

const empty = {
  name: "",
  email: "",
  phone: "",
  company: "",
  project_type: "Website",
  budget: "Not sure yet",
  completion_date: "",
  current_website: "No",
  message: "",
  attachments: [],
};

export default function ContactForm() {
  const [form, setForm] = useState(empty);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("idle");
  const [otpCode, setOtpCode] = useState("");
  const [authError, setAuthError] = useState("");
  const [isAuthed, setIsAuthed] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [authMethod, setAuthMethod] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [submittedName, setSubmittedName] = useState("");
  const [submittedCompany, setSubmittedCompany] = useState("");
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    base44.auth.isAuthenticated().then(setIsAuthed);
  }, []);

  const createInquiry = async (formData) => {
    const attachmentUrls = (formData.attachments || []).map((a) =>
      typeof a === "string" ? a : a.url
    );
    await base44.functions.invoke("submit-inquiry", {
      ...formData,
      attachments: attachmentUrls,
    });
    base44.analytics.track({ eventName: "inquiry_submitted" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setAuthError("");
    setStatus("submitting");
    try {
      await createInquiry(form);
      setSubmittedEmail(form.email);
      setSubmittedName(form.name);
      setSubmittedCompany(form.company);
      setStatus("done");
      setForm(empty);
    } catch (err) {
      setAuthError(err?.message || "Something went wrong. Please try again.");
      setStatus("idle");
    }
  };

  // Optional account creation from success screen
  const createAccount = async () => {
    setAuthError("");
    if (!password) {
      setAuthError("Set a password or use Google/Facebook.");
      return;
    }
    if (password !== confirmPassword) {
      setAuthError("Passwords do not match.");
      return;
    }
    try {
      await base44.auth.register({ email: submittedEmail, password });
      setStatus("otp");
    } catch (err) {
      const msg = err?.message || "";
      setAuthError(
        msg.toLowerCase().includes("already")
          ? "An account with this email already exists. Try signing in instead."
          : msg || "Registration failed."
      );
    }
  };

  const verifyOtp = async () => {
    setAuthError("");
    setStatus("verifying");
    try {
      const result = await base44.auth.verifyOtp({ email: submittedEmail, otpCode });
      if (result?.access_token) {
        base44.auth.setToken(result.access_token);
      }
      try {
        await base44.auth.updateMe({
          display_name: submittedName,
          company: submittedCompany,
        });
      } catch {
        /* profile update is best-effort */
      }
      setAuthMethod("password");
      setAccountCreated(true);
      setIsAuthed(true);
      setStatus("done");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      setAuthError(err?.message || "Invalid verification code.");
      setStatus("otp");
    }
  };

  const handleResend = async () => {
    try {
      await base44.auth.resendOtp(submittedEmail);
    } catch {
      /* best-effort */
    }
  };

  const handleOAuth = (provider) => {
    base44.auth.loginWithProvider(provider, window.location.pathname);
  };

  // OTP verification step (optional account creation)
  if (status === "otp" || status === "verifying") {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Mail size={36} className="mx-auto text-accent-foreground/80" />
          <h3 className="mt-4 font-heading font-bold text-xl text-accent-foreground">
            Verify your email
          </h3>
          <p className="mt-2 text-sm text-accent-foreground/70">
            We sent a code to {submittedEmail}
          </p>
        </div>
        {authError && (
          <p className="text-sm text-red-300 text-center">{authError}</p>
        )}
        <div className="flex justify-center">
          <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
        <button
          onClick={verifyOtp}
          disabled={status === "verifying" || otpCode.length < 6}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-accent-foreground text-accent font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {status === "verifying" ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Verifying...
            </>
          ) : (
            "Verify & create account"
          )}
        </button>
        <p className="text-center text-sm text-accent-foreground/60">
          Didn't receive the code?{" "}
          <button onClick={handleResend} className="font-semibold underline">
            Resend
          </button>
        </p>
      </div>
    );
  }

  // Success step
  if (status === "done") {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center py-12">
        <CheckCircle2 size={48} className="text-accent-foreground/90" />
        <h3 className="mt-5 font-heading font-bold text-2xl text-accent-foreground">
          Thanks — we got it.
        </h3>
        <p className="mt-3 text-accent-foreground/70 max-w-sm">
          Your message is on its way. We'll reach out within one business day.
        </p>
        {accountCreated && (
          <p className="mt-3 text-sm text-accent-foreground/60 max-w-sm">
            We've created your account. Next time, sign in with your{" "}
            {authMethod === "password"
              ? "email and password"
              : "Google or Facebook"}{" "}
            to access your project details.
          </p>
        )}

        {/* Optional account creation */}
        {!isAuthed && !accountCreated && (
          <div className="mt-8 w-full max-w-sm text-left">
            <div className="pt-6 border-t border-accent-foreground/15">
              <h3 className="font-heading font-bold text-accent-foreground mb-1 text-center">
                Create an account (optional)
              </h3>
              <p className="text-sm text-accent-foreground/60 mb-4 text-center">
                Save your details to track your project and access proposals
                and invoices online.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Password">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="hd-input"
                  autoComplete="new-password"
                />
              </Field>
              <Field label="Confirm">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="hd-input"
                  autoComplete="new-password"
                />
              </Field>
            </div>
            {authError && (
              <p className="text-sm text-red-300 mt-2">{authError}</p>
            )}
            <button
              onClick={createAccount}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-accent-foreground text-accent font-semibold hover:opacity-90 transition-opacity"
            >
              Create account
            </button>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleOAuth("google")}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-card text-foreground font-medium text-sm hover:opacity-90 transition-opacity"
              >
                <GoogleIcon className="w-5 h-5" />
                Google
              </button>
              <button
                type="button"
                onClick={() => handleOAuth("facebook")}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-blue-600 text-white font-medium text-sm hover:opacity-90 transition-opacity"
              >
                <Facebook size={18} />
                Facebook
              </button>
            </div>
          </div>
        )}

        <button
          onClick={() => {
            setStatus("idle");
            setAccountCreated(false);
            setAuthMethod("");
            setAuthError("");
            setPassword("");
            setConfirmPassword("");
          }}
          className="mt-8 px-6 py-3 rounded-full bg-accent-foreground/10 text-accent-foreground font-semibold hover:bg-accent-foreground/15 transition-colors"
        >
          Send another
        </button>
      </div>
    );
  }

  // Main form
  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Your name">
          <input
            value={form.name}
            onChange={update("name")}
            placeholder="Jane Smith"
            className="hd-input"
          />
        </Field>
        <Field label="Company">
          <input
            value={form.company}
            onChange={update("company")}
            placeholder="Smith & Co."
            className="hd-input"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Email" required>
          <input
            required
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="jane@business.com"
            className="hd-input"
          />
        </Field>
        <Field label="Phone" required>
          <input
            required
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            placeholder="(859) 555-1234"
            className="hd-input"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Type of Project">
          <select
            value={form.project_type}
            onChange={update("project_type")}
            className="hd-input"
          >
            <option>Website</option>
            <option>Web App</option>
            <option>Mobile App</option>
            <option>SEO</option>
            <option>Consulting</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Estimated Budget">
          <select
            value={form.budget}
            onChange={update("budget")}
            className="hd-input"
          >
            <option>Under $5k</option>
            <option>$5k - $15k</option>
            <option>$15k - $40k</option>
            <option>$40k+</option>
            <option>Not sure yet</option>
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Desired Completion Date">
          <input
            type="date"
            value={form.completion_date}
            onChange={update("completion_date")}
            className="hd-input"
          />
        </Field>
        <Field label="Current Website?">
          <select
            value={form.current_website}
            onChange={update("current_website")}
            className="hd-input"
          >
            <option>No</option>
            <option>Yes</option>
          </select>
        </Field>
      </div>

      <Field label="Describe your goals" required>
        <textarea
          required
          value={form.message}
          onChange={update("message")}
          rows={4}
          placeholder="Tell us what you're trying to build, grow, or fix..."
          className="hd-input resize-none"
        />
      </Field>

      <Field label="Attachments (optional)">
        <FileUpload
          attachments={form.attachments}
          onChange={(files) => setForm((f) => ({ ...f, attachments: files }))}
        />
      </Field>

      {authError && (
        <p className="text-sm text-red-300">{authError}</p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-accent-foreground text-accent font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
      >
        {status === "submitting" ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send size={18} />
            Contact Us
          </>
        )}
      </button>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-accent-foreground/80 mb-2">
        {label}
        {required && <span className="text-accent-foreground/70"> *</span>}
      </span>
      {children}
    </label>
  );
}